import {useMutation, useQuery, useQueryClient} from "react-query";
import axios, { AxiosError } from "axios";
import {useSnackbar} from "notistack";
import {LocalDateTimeFormatISO} from "../../../../Utils/DateFormatPatternUtils";

const apiUrl = "api/generation-inverter";
const stringApiUrl = "api/equipment/string-current"

const fetchGenerationInverterByDaily = async (startDateTime: any, endDateTime: any, groupBy: any, powerStationId: number, equipmentId: number) => {
    try {
        return await axios.get(`${apiUrl}/${groupBy}`,
            {
                params: {
                    startDateTime: LocalDateTimeFormatISO(startDateTime),
                    endDateTime: LocalDateTimeFormatISO(endDateTime),
                    powerStationId: powerStationId,
                    equipmentId: equipmentId
                }
            }).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        return Promise.reject(error);
    }
};

export const FindGenerationInverterByDailReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: ({startDateTime, endDateTime, groupBy,  powerStationId, equipmentId}:any) => {
            return fetchGenerationInverterByDaily(startDateTime, endDateTime, groupBy,  powerStationId, equipmentId);
        },
        onError: (error: any) => {
            enqueueSnackbar(`Erro ao carregar dados de geração de energia: ${error.message}`, { variant: "error" });
        }
    })
};

const fetchTeleObjectInverter = async (equipmentId: number, powerStationId: number) => {
    try {
        return await axios.get(`api/teleobject/inverter-object-type/${equipmentId}/${powerStationId}`)
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        return Promise.reject(error);
    }
};

const fetchStrintCurrentByEquipmentId = async (equipmentId: number, startDateTime: any, endDateTime: any) => {
    try {
        return await axios.get(`${stringApiUrl}/${equipmentId}?startDateTime=${startDateTime}&endDateTime=${endDateTime}`)
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

export const FindTeleObjectInverterReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: ({equipmentId, powerStationId}:any) => {
            return fetchTeleObjectInverter(equipmentId, powerStationId);
        },
        onError: (error: any) => {
            enqueueSnackbar(`Erro ao carregar dados de inversor: ${error.message}`, { variant: "error" });
        }
    })
};

const fetchPowerVSIrradianceDataByEquipmentIdAndByHour = async (
    powerStationId: number,
    equipmentId: number,
    startDateTime: any,
    endDateTime: any) => {

    const formattedStartDateTime = LocalDateTimeFormatISO(startDateTime);
    const formattedEndDateTime = LocalDateTimeFormatISO(endDateTime);


    const { data } = await axios.get(`/api/power-irradiance/hour/inverters`, {
        params: {
            powerStationId,
            equipmentId,
            startDateTime: formattedStartDateTime,
            endDateTime: formattedEndDateTime
        }
    });

    return data;
};

export const FindPowerVSIrradianceDataByEquipmentIdAndByHour = (
    powerStationId: number,
    equipmentId: number,
    startDateTime: any,
    endDateTime: any
) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery(
        ['powerIrradiance', powerStationId, equipmentId, startDateTime, endDateTime],
        async () => {
            return fetchPowerVSIrradianceDataByEquipmentIdAndByHour(
                powerStationId,
                equipmentId,
                startDateTime,
                endDateTime
            );
        },
        {
            enabled: !!powerStationId && equipmentId > 0 && !!startDateTime && !!endDateTime, // Executa apenas se todos os parâmetros estiverem definidos
            onError: (error: any) => {
                enqueueSnackbar(
                    `Erro ao carregar dados de potência vs irradiância: ${error.message}`,
                    { variant: 'error' }
                );
            },
        }
    );
};


export const FindStringCurrentByEquipmentId = (
    equipmentId: number,
    startDateTime: any,
    endDateTime: any
) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery(
        ['currentString', equipmentId, startDateTime, endDateTime],
        async () => {
            return fetchStrintCurrentByEquipmentId(
                equipmentId,
                startDateTime,
                endDateTime
            );
        },
        {
            enabled: equipmentId > 0 && !!equipmentId && !!startDateTime && !!endDateTime, // Executa apenas se todos os parâmetros estiverem definidos
            onError: (error: any) => {
                enqueueSnackbar(
                    'Corrente de String não encontradas. Verificar tipos de objeto no cadastro de teleobjetos.',
                    { variant: 'error' }
                );
            },
        }
    );
};

/** Nominal power + optional max string current (A). Do not default maxStringCurrent to 20. */
export interface NominalPowerData {
    id?: number;
    description?: string;
    nominalPowerValue?: number | null;
    maxStringCurrent?: number | null;
}

export interface NominalPowerResponse {
    data?: NominalPowerData;
    message?: string;
}

const fetchNominalPowerByEquipmentId = async (equipmentId: number): Promise<NominalPowerResponse> => {
    try {
        return await axios.get(`api/equipment/nominal-power/${equipmentId}`)
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching nominal power data:", error);
        return Promise.reject(error);
    }
};

export const FindNominalPowerByEquipmentIdReactQuery = (equipmentId: number) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery(
        ['nominalPower', equipmentId],
        async () => {
            return fetchNominalPowerByEquipmentId(equipmentId);
        },
        {
            enabled: equipmentId > 0 && !!equipmentId,
            onError: (error: any) => {
                enqueueSnackbar(
                    `Erro ao carregar potência nominal: ${error.message}`,
                    { variant: 'error' }
                );
            },
            retry: 3,
        }
    );
};

export const getMaxStringCurrentFromNominalPower = (
    response: NominalPowerResponse | undefined
): number | null => {
    const value = response?.data?.maxStringCurrent;
    if (value === null || value === undefined) {
        return null;
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
};

export interface MaxStringCurrentUpdateResponse {
    data?: { equipmentId?: number; maxStringCurrent?: number | null };
    message?: string;
}

interface MaxStringCurrentUpdateErrorResponse {
    detail?: string;
    message?: string;
}

const updateMaxStringCurrentByEquipmentId = async (
    equipmentId: number,
    maxStringCurrent: number
): Promise<MaxStringCurrentUpdateResponse> => {
    const res = await axios.put(`api/equipment/max-string-current/${equipmentId}`, {
        maxStringCurrent,
    });
    return res.data;
};

export const useUpdateMaxStringCurrentMutation = (equipmentId: number) => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    return useMutation<MaxStringCurrentUpdateResponse, AxiosError<MaxStringCurrentUpdateErrorResponse>, number>(
        (maxStringCurrent: number) => updateMaxStringCurrentByEquipmentId(equipmentId, maxStringCurrent),
        {
            onSuccess: async (response, maxStringCurrent) => {
                const saved =
                    response?.data?.maxStringCurrent != null
                        ? Number(response.data.maxStringCurrent)
                        : maxStringCurrent;
                queryClient.setQueryData(
                    ['nominalPower', equipmentId],
                    (old: NominalPowerResponse | undefined) => ({
                        message: old?.message ?? 'Data found',
                        data: {
                            ...(old?.data ?? {}),
                            maxStringCurrent: saved,
                        },
                    })
                );
                await queryClient.invalidateQueries(['nominalPower', equipmentId], { refetchActive: true });
                enqueueSnackbar('Corrente máxima salva com sucesso.', { variant: 'success' });
            },
            onError: (error) => {
                const detail =
                    error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    error?.message ||
                    'Erro ao salvar corrente máxima.';
                enqueueSnackbar(detail, { variant: 'error' });
            },
        }
    );
};

export interface ActiveTeleObjectAlarm {
    teleObjectId: number;
    severityId: number;
}

export interface ActiveTeleObjectAlarmsResponse {
    message?: string;
    data?: ActiveTeleObjectAlarm[];
}

const fetchActiveTeleObjectAlarmsByEquipmentId = async (
    equipmentId: number
): Promise<ActiveTeleObjectAlarmsResponse> => {
    const res = await axios.get(`api/equipment/${equipmentId}/active-teleobject-alarms`);
    return res.data;
};

export const NON_APPLICABLE_SEVERITY_ID = 5;
export const ACTIVE_TELEOBJECT_ALARMS_ERROR_MESSAGE =
    'Não foi possível carregar o estado dos alarmes dos teleobjetos. Tente novamente.';

export const FindActiveTeleObjectAlarmsByEquipmentId = (equipmentId: number) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery(
        ['activeTeleObjectAlarms', equipmentId],
        async () => fetchActiveTeleObjectAlarmsByEquipmentId(equipmentId),
        {
            enabled: equipmentId > 0,
            retry: 1,
            onError: () => {
                enqueueSnackbar(ACTIVE_TELEOBJECT_ALARMS_ERROR_MESSAGE, {
                    variant: 'error',
                });
            },
        }
    );
};

export const getAlarmedTeleObjectIdSet = (
    response: ActiveTeleObjectAlarmsResponse | undefined
): Set<number> => {
    const list = response?.data;
    if (!Array.isArray(list)) {
        return new Set();
    }
    return new Set(
        list
            .filter(
                (item) =>
                    Number(item?.severityId) !== NON_APPLICABLE_SEVERITY_ID
            )
            .map((item) => Number(item?.teleObjectId))
            .filter((id) => Number.isFinite(id) && id > 0)
    );
};

/** Last-value snapshot for gauges (SGD-1933 applied on backend). Charts keep `string-current`. */
export interface StringCurrentSnapshotResponse {
    message?: string;
    data?: Array<{
        teleObjectId: number;
        name: string;
        dateTime?: string | null;
        value?: number | null;
    }>;
}

const fetchStringCurrentSnapshotByEquipmentId = async (
    equipmentId: number
): Promise<StringCurrentSnapshotResponse> => {
    const res = await axios.get(`api/equipment/string-current-snapshot/${equipmentId}`);
    return res.data;
};

export const FindStringCurrentSnapshotByEquipmentId = (equipmentId: number) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery(
        ['stringCurrentSnapshot', equipmentId],
        async () => fetchStringCurrentSnapshotByEquipmentId(equipmentId),
        {
            enabled: equipmentId > 0,
            retry: 1,
            onError: () => {
                enqueueSnackbar(
                    'Corrente de String não encontradas. Verificar tipos de objeto no cadastro de teleobjetos.',
                    { variant: 'error' }
                );
            },
        }
    );
};

export interface InstantInfoValue {
    teleObjectId: number | null;
    name: string | null;
    value: number | null;
    unityName: string | null;
    teleObjectDataTypeId: number | null;
}

export interface InverterInstantInfoData {
    dcSideDataList: InstantInfoValue[];
    acVoltageDataList: InstantInfoValue[];
    acCurrentDataList: InstantInfoValue[];
    operatingDataList: InstantInfoValue[];
}

export interface InverterInstantInfoResponse {
    message?: string;
    data?: InverterInstantInfoData;
}

const fetchInverterInstantInfo = async (
    equipmentId: number,
    powerStationId: number
): Promise<InverterInstantInfoResponse> => {
    const res = await axios.get(`api/teleobject/instant-info/${equipmentId}/${powerStationId}`);
    return res.data;
};

export const FindInverterInstantInfoByEquipmentId = (
    equipmentId: number,
    powerStationId: number | undefined,
    refetchInterval: number
) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery(
        ['inverterInstantInfo', equipmentId, powerStationId],
        async () => fetchInverterInstantInfo(equipmentId, powerStationId as number),
        {
            enabled: equipmentId > 0 && !!powerStationId,
            retry: 1,
            refetchInterval,
            onError: () => {
                enqueueSnackbar(
                    'Não foi possível carregar as informações instantâneas do inversor. Tente novamente.',
                    { variant: 'error' }
                );
            },
        }
    );
};

export interface InverterIndicatorsResponse {
    message?: string;
    data?: InstantInfoValue[];
}

const fetchInverterIndicators = async (
    equipmentId: number,
    powerStationId: number
): Promise<InverterIndicatorsResponse> => {
    const res = await axios.get(`api/teleobject/indicators/${equipmentId}/${powerStationId}`);
    return res.data;
};

export const FindInverterIndicatorsByEquipmentId = (
    equipmentId: number,
    powerStationId: number | undefined,
    refetchInterval: number
) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery(
        ['inverterIndicators', equipmentId, powerStationId],
        async () => fetchInverterIndicators(equipmentId, powerStationId as number),
        {
            enabled: equipmentId > 0 && !!powerStationId,
            retry: 1,
            refetchInterval,
            onError: () => {
                enqueueSnackbar(
                    'Não foi possível carregar os indicadores do inversor. Tente novamente.',
                    { variant: 'error' }
                );
            },
        }
    );
};
